/**
 * Pagination — the one pager (design-grammar §4.7).
 *
 * Replaces three idioms that disagreed about everything: `CommunityPage`'s
 * centred 1-based "Page N of M", `UserRoster`'s 0-based
 * "Page {page + 1} of {totalPages} ({total} total)", and `DonorCRMPage`'s
 * "← Prev / Next →" beside a hand-written "Showing 9 of 214 donors".
 *
 * The range line, not the page number, is the honest form: "Showing 21–40 of
 * 214 donors" says how much of the set the reader is looking at, which is the
 * question a truncated list has to answer out loud (the "showing 20 of 48"
 * contract). "Page 2 of 6" is derivable from it and answers less.
 *
 * Rules the primitive owns, so no caller re-decides them:
 *   - `page` is 1-based at the boundary. A 0-based caller adds 1 once, here,
 *     rather than in three places on screen.
 *   - A single page renders nothing. The list's own header already states the
 *     total, and a pager with both arrows dead is furniture.
 *   - Without `onPageChange` the arrows render disabled — a mock-backed list
 *     (`DonorCRMPage`) shows its true shape and cannot pretend to move.
 */
import { useTranslation } from 'react-i18next';
import type { TKey } from '@/i18n/msg';
import { PillButton } from './PillButton';
import { MonoLabel } from './MonoLabel';
import { cn } from '@/lib/utils';
import { COARSE_HIT_Y_28 } from '@/lib/touch-target';

export interface PaginationProps {
  /** 1-based index of the page on screen. */
  page: number;
  /** Rows per page — the `limit` the query asked for. */
  limit: number;
  /** Rows across every page, from the SAME filtered query the rows came from. */
  total: number;
  /** Omit for a list that cannot actually page (mock data): arrows go inert. */
  onPageChange?: (page: number) => void;
  /**
   * A count-aware catalog key (a plural BASE, `_one`/`_other` in en) that
   * renders the total with its noun: `'common:pagination.users'` →
   * "Showing 21–40 of 214 users". It replaced the English plural `noun`
   * prop (#2109 R3 L0): the count picks the plural form per language. Omit
   * it and the line ends at the number.
   */
  itemsKey?: TKey;
  className?: string;
}

export function Pagination({
  page,
  limit,
  total,
  onPageChange,
  itemsKey,
  className,
}: PaginationProps) {
  const { t } = useTranslation('common');
  const totalPages = limit > 0 ? Math.ceil(total / limit) : 0;
  if (totalPages <= 1) return null;

  const current = Math.min(Math.max(page, 1), totalPages);
  const from = (current - 1) * limit + 1;
  const to = Math.min(current * limit, total);

  const go = (next: number) => {
    if (!onPageChange) return;
    onPageChange(Math.min(Math.max(next, 1), totalPages));
  };

  return (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      <MonoLabel className="font-normal tabular-nums">
        {itemsKey
          ? t('pagination.rangeOfItems', {
              from,
              to,
              items: (t as unknown as (k: string, o: object) => string)(itemsKey, { count: total }),
            })
          : t('pagination.range', { from, to, total })}
      </MonoLabel>
      {/* #2602 (D-2420): the 28 px pills reach 8 px above and below on a coarse
          pointer; they are wide enough already, so the row's width never moves. */}
      <div className="isolate flex items-center gap-2">
        <PillButton
          variant="ghost"
          className={cn('px-3.5 py-1.5 text-caption', COARSE_HIT_Y_28)}
          onClick={() => go(current - 1)}
          disabled={!onPageChange || current <= 1}
        >
          {t('actions.previous')}
        </PillButton>
        <PillButton
          variant="ghost"
          className={cn('px-3.5 py-1.5 text-caption', COARSE_HIT_Y_28)}
          onClick={() => go(current + 1)}
          disabled={!onPageChange || current >= totalPages}
        >
          {t('actions.next')}
        </PillButton>
      </div>
    </div>
  );
}
